import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, Globe, ShoppingCart, Layers, Image, Smartphone, Link2, BookOpen, ChevronDown, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import ContactForm from '../components/ContactForm';

interface Service {
  icon: React.ReactNode;
  title: string;
  tagline: string;
  description: string;
  bullets: string[];
  color: string;
}

const services: Service[] = [
  {
    icon: <Brain size={24} />,
    title: 'Custom AI',
    tagline: 'Intelligent automation built for your workflow',
    description: 'We design and deploy AI-powered solutions tailored specifically to your business—from intelligent chatbots and content generators to predictive analytics and machine learning models.',
    bullets: ['Custom GPT & chatbot development', 'Agentic workflows & multi-step AI automation', 'Predictive analytics & ML models', 'AI content generation pipelines', 'Integration with existing systems'],
    color: '#7c3aed',
  },
  {
    icon: <Globe size={24} />,
    title: 'Web Development',
    tagline: 'Fast, beautiful, and scalable websites',
    description: 'From sleek landing pages to complex web applications, we build responsive, high-performing websites using modern frameworks with intuitive UX and scalable architecture.',
    bullets: ['React, Next.js & TypeScript', 'SEO-optimized architecture', 'Performance-first approach', 'CMS integration & custom backends'],
    color: '#2563eb',
  },
  {
    icon: <ShoppingCart size={24} />,
    title: 'E-Commerce',
    tagline: 'Stores that convert visitors into customers',
    description: 'Full-featured e-commerce platforms with custom product management, secure payment gateways, inventory automation, and customer retention strategies.',
    bullets: ['Custom storefronts & product pages', 'Secure payment gateway integration', 'Inventory & order management', 'Abandoned cart & retention flows'],
    color: '#059669',
  },
  {
    icon: <Layers size={24} />,
    title: 'Web3',
    tagline: 'Decentralized experiences for the next internet',
    description: 'Build decentralized, blockchain-based features including DApps, smart contract integration, and token economy design for the new web.',
    bullets: ['DApp development & deployment', 'Smart contract integration', 'Token economy design', 'Wallet connect & Web3 onboarding'],
    color: '#6d4aff',
  },
  {
    icon: <Image size={24} />,
    title: 'NFTs',
    tagline: 'End-to-end NFT collection creation and launch',
    description: 'From concept to market: we handle NFT collection creation, smart contract deployment, digital wallet integration, community-building, and marketplace listings.',
    bullets: ['Collection design & minting', 'Smart contract deployment', 'Marketplace listing (OpenSea, etc.)', 'Community & Discord setup'],
    color: '#db2777',
  },
  {
    icon: <Smartphone size={24} />,
    title: 'App Creation',
    tagline: 'Native & cross-platform mobile experiences',
    description: 'We build iOS, Android, and web apps with a focus on speed, security, and usability—from prototyping and UI/UX design through testing and app store publishing.',
    bullets: ['iOS & Android development', 'Cross-platform (React Native)', 'UI/UX prototyping & design', 'App store submission & ASO'],
    color: '#0891b2',
  },
  {
    icon: <Link2 size={24} />,
    title: 'Blockchain',
    tagline: 'Custom ledgers and decentralized infrastructure',
    description: 'Custom blockchain solutions including ledger design, smart contract implementation, decentralized application launches, and enterprise-grade security.',
    bullets: ['Custom blockchain architecture', 'Smart contract development & audits', 'DeFi protocol integration', 'Enterprise security & compliance'],
    color: '#7c3aed',
  },
  {
    icon: <BookOpen size={24} />,
    title: 'E-Book Publishing',
    tagline: 'From manuscript to published e-book',
    description: 'Full-service e-book publishing from manuscript to distribution—formatting, design, ISBN registration, and placement across Apple Books, Amazon Kindle, and more.',
    bullets: ['Professional formatting & layout', 'Cover design & branding', 'ISBN registration & metadata', 'Multi-platform distribution'],
    color: '#be123c',
  },
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const Services: React.FC = () => {
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <>
      <Helmet>
        <title>Services | Elola Technologies – Web3, AI, Apps & Digital Development</title>
        <meta name="description" content="Custom services including Web3, AI tools, NFTs, e-commerce, blockchain, app development, e-book publishing, and product sourcing by Elola Technologies." />
        <link rel="canonical" href="https://elola.tech/services" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Elola Technologies" />
        <meta property="og:title" content="Services | Elola Technologies – AI, Web3, Apps & More" />
        <meta property="og:description" content="Custom digital services from AI to blockchain. Elola Technologies builds modern solutions for ambitious brands." />
        <meta property="og:image" content="https://elola.tech/preview-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content="https://elola.tech/services" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Services | Elola Technologies – AI, Web3, Apps & More" />
        <meta name="twitter:description" content="Custom digital services from AI to blockchain. Elola Technologies builds modern solutions for ambitious brands." />
        <meta name="twitter:image" content="https://elola.tech/preview-image.png" />
      </Helmet>

      <div style={{ background: 'var(--bg-base)', minHeight: '100vh', paddingTop: 'var(--navbar-height)' }}>
        {/* Header */}
        <section style={{
          padding: 'clamp(50px, 7vw, 90px) var(--page-padding-x) clamp(40px, 5vw, 70px)',
          textAlign: 'center',
          background: 'linear-gradient(180deg, var(--bg-surface) 0%, var(--bg-base) 100%)',
          borderBottom: '1px solid var(--border-card)',
        }}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="section-tag" style={{ justifyContent: 'center' }}>
              <Zap size={13} />
              What We Build
            </div>
            <h1 style={{ fontSize: 'var(--text-h1)', fontWeight: 800, marginBottom: '16px' }}>
              Our <span className="gradient-text">Services</span>
            </h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: 'clamp(15px, 1.5vw, 17px)', maxWidth: '520px', margin: '0 auto' }}>
              From AI to blockchain to beautifully crafted websites—we deliver end-to-end digital solutions that drive real results.
            </p>
          </motion.div>
        </section>

        {/* Services Grid */}
        <section style={{ padding: 'clamp(48px, 6vw, 80px) var(--page-padding-x)', maxWidth: 'var(--page-max-width)', margin: '0 auto' }}>
          <motion.div
            style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
          >
            {services.map((service) => {
              const isOpen = expanded === service.title;
              return (
                <motion.div
                  key={service.title}
                  variants={itemVariants}
                  style={{
                    background: 'var(--bg-card)',
                    border: `1px solid ${isOpen ? 'var(--border-subtle)' : 'var(--border-card)'}`,
                    borderRadius: '14px',
                    overflow: 'hidden',
                    transition: 'border-color 0.2s',
                  }}
                >
                  <button
                    onClick={() => setExpanded(isOpen ? null : service.title)}
                    aria-expanded={isOpen}
                    style={{
                      width: '100%',
                      padding: '22px 24px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      background: 'none',
                      color: 'var(--text-primary)',
                    }}
                  >
                    <div style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '12px',
                      background: `${service.color}20`,
                      border: `1px solid ${service.color}30`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: service.color,
                      flexShrink: 0,
                    }}>
                      {service.icon}
                    </div>
                    <div style={{ flex: 1 }}>
                      <h2 style={{ fontSize: '17px', fontWeight: 700, marginBottom: '2px' }}>{service.title}</h2>
                      <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{service.tagline}</p>
                    </div>
                    <motion.div
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      transition={{ duration: 0.25 }}
                      style={{ color: 'var(--text-muted)', flexShrink: 0 }}
                    >
                      <ChevronDown size={20} />
                    </motion.div>
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: 'easeOut' }}
                        style={{ overflow: 'hidden' }}
                      >
                        <div style={{
                          padding: '0 24px 24px 84px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '16px',
                        }}>
                          <p style={{ color: 'var(--text-secondary)', fontSize: '15px', lineHeight: '1.7' }}>
                            {service.description}
                          </p>
                          <ul style={{ display: 'flex', flexDirection: 'column', gap: '8px', listStyle: 'none' }}>
                            {service.bullets.map(b => (
                              <li key={b} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                                <span style={{
                                  width: '6px', height: '6px', borderRadius: '50%',
                                  background: service.color, flexShrink: 0,
                                }} />
                                {b}
                              </li>
                            ))}
                          </ul>
                          <Link
                            to="/contact"
                            className="btn btn-primary"
                            style={{ padding: '10px 22px', fontSize: '14px', alignSelf: 'flex-start' }}
                          >
                            <Zap size={14} />
                            Get Started
                          </Link>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </motion.div>
        </section>

        {/* CTA + Contact Form */}
        <section style={{
          padding: 'clamp(48px, 6vw, 80px) var(--page-padding-x)',
          background: 'var(--bg-surface)',
          borderTop: '1px solid var(--border-card)',
        }}>
          <div style={{ maxWidth: '680px', margin: '0 auto' }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              style={{ textAlign: 'center', marginBottom: '40px' }}
            >
              <h2 style={{ fontSize: 'var(--text-h2)', fontWeight: 800, marginBottom: '12px' }}>
                Ready to get started?
              </h2>
              <p style={{ color: 'var(--text-secondary)', fontSize: '16px' }}>
                Tell us what you need and we'll craft a solution around your goals.
              </p>
            </motion.div>
            <ContactForm />
          </div>
        </section>
      </div>
    </>
  );
};

export default Services;
